/**
 * Which payments runtime the card forms talk to — **the switch** (M7.7).
 *
 * A "runtime" is the pair, because the two halves have to agree: a mocked API with a real
 * vendor iframe would tokenize a real card into a fake vault, and a real API with a fake
 * tokenizer would send `nonce-mock…` to a processor. They are chosen together, once, at
 * module load.
 *
 * | Build | Environment | Client | Tokenizer |
 * |---|---|---|---|
 * | any | `VITE_USE_MOCKS=true` | the in-memory vault | the fake panel — no script, no iframe |
 * | `vite dev` only | `VITE_FAKE_TOKENIZER=true` | the real API | the fake panel |
 * | `vite dev` / `vite build` | neither | the real API | accept.blue Hosted Tokenization v0.3 |
 *
 * The middle row is the **dev walk against the real API**: the API's own accept.blue fake
 * takes any `nonce-…`, so a real vendor iframe would add a vendor account and a network
 * round trip to a flow that is already end to end. It is refused outside `vite dev` —
 * `import.meta.env.DEV` is compiled to `false` in a bundle — because a production build
 * that sent `nonce-mock…` to a real processor would fail every card at the counter.
 *
 * ## No mock fallback, unlike the admin seams
 *
 * `apps/admin`'s clients fall back to fixtures the first time a call cannot reach the API
 * in a dev build. This one does not, at any time. A card form that answered "saved" from
 * a fixture would tell a patient their card is on file when nothing is: that is a lie
 * about money, and the clinic would discover it at the moment it tried to charge
 * (rules §17.4, §35.8). An unreachable API is an error the patient can act on — try again
 * or call the clinic — and it is shown as one.
 *
 * ## The default is inert
 *
 * The context's default value is `INERT_PAYMENTS`: a client that answers `enabled: false`
 * and a tokenizer that refuses to mount. It makes **no request** and loads **no script**.
 * So a component rendered without a provider — every funnel unit test, and any future
 * static render — behaves exactly as it does for a clinic that takes no cards, and
 * "does this page reach the payment vendor?" stays a property of the app shell that
 * installs the runtime, exactly as `useFunnelBoot` makes the draft transport a property
 * of the route (`Docs/BOOKING_FUNNEL_WIRING.md` §3.1).
 */

import { createContext, useContext, type ReactNode } from 'react';

import { paymentsClient, type PaymentsClient } from './payments';
import { mountHostedTokenizer, type MountTokenizer } from './payments-tokenizer';
import { createMockPaymentsClient, mountFakeTokenizer } from './payments.mock';

export interface PaymentsRuntime {
  client: PaymentsClient;
  mountTokenizer: MountTokenizer;
}

const useMocks = import.meta.env.VITE_USE_MOCKS === 'true';
const fakeTokenizer = import.meta.env.DEV && import.meta.env.VITE_FAKE_TOKENIZER === 'true';

/** The app's runtime, created once. The provider and tests override it. */
export const paymentsRuntime: PaymentsRuntime = useMocks
  ? { client: createMockPaymentsClient(), mountTokenizer: mountFakeTokenizer }
  : {
      client: paymentsClient,
      mountTokenizer: fakeTokenizer ? mountFakeTokenizer : mountHostedTokenizer,
    };

/** A clinic that takes no cards, and a vendor that is never reached. */
export const INERT_PAYMENTS: PaymentsRuntime = {
  client: {
    config: () =>
      Promise.resolve({
        enabled: false,
        provider: null,
        publicKey: null,
        scriptUrl: null,
        sandbox: true,
      }),
    saveDraftCard: () => Promise.reject(new Error('payments are not installed')),
    saveBookingCard: () => Promise.reject(new Error('payments are not installed')),
    getCardOnFile: () => Promise.resolve(null),
    saveCardOnFile: () => Promise.reject(new Error('payments are not installed')),
    removeCardOnFile: () => Promise.resolve(),
  },
  mountTokenizer: () => Promise.reject(new Error('payments are not installed')),
};

const PaymentsRuntimeContext = createContext<PaymentsRuntime>(INERT_PAYMENTS);

export function PaymentsRuntimeProvider({
  runtime,
  children,
}: {
  runtime: PaymentsRuntime;
  children: ReactNode;
}) {
  return (
    <PaymentsRuntimeContext.Provider value={runtime}>{children}</PaymentsRuntimeContext.Provider>
  );
}

export function usePaymentsRuntime(): PaymentsRuntime {
  return useContext(PaymentsRuntimeContext);
}
